import { useState, useEffect, useMemo } from 'react';
import { useLazyBody } from './useLazyBody';
import { useRequestBodies } from './useRequestBodies';
import type { HttpRequest } from '../types/http-request';

type UseResolvedBodiesResult = {
  requestBody: string;
  responseBody: string;
};

export function useResolvedBodies(
  request: HttpRequest | undefined | null,
): UseResolvedBodiesResult {
  const { fetchBodies } = useRequestBodies();

  const requestLazyBody = useLazyBody({
    bodyRef: request?.bodyRef,
    bodyPreview: request?.bodyPreview,
    inlineBody: request?.body,
    bodyStorageType: request?.bodyStorageType,
    bodyContentType: request?.bodyContentType,
    bodySize: request?.bodySize,
  });

  const responseLazyBody = useLazyBody({
    bodyRef: request?.responseBodyRef,
    bodyPreview: request?.responseBodyPreview,
    inlineBody: request?.responseBody,
    bodyStorageType: request?.responseBodyStorageType,
    bodyContentType: request?.responseBodyContentType,
    bodySize: request?.responseBodySize,
  });

  const [dbBodies, setDbBodies] = useState<{
    requestBody?: string;
    responseBody?: string;
  }>({});

  useEffect(() => {
    const requestId = request?.id;
    if (!requestId) {
      setDbBodies({});
      return;
    }

    const hasFileStoredRequest =
      request.bodyStorageType === 'file' || !!request.bodyRef;
    const hasFileStoredResponse =
      request.responseBodyStorageType === 'file' || !!request.responseBodyRef;

    setDbBodies({});

    if (hasFileStoredRequest && hasFileStoredResponse) {
      return;
    }

    let cancelled = false;
    fetchBodies(requestId)
      .then((bodies) => {
        if (!cancelled && bodies) {
          setDbBodies({
            requestBody: hasFileStoredRequest ? undefined : bodies.requestBody,
            responseBody: hasFileStoredResponse
              ? undefined
              : bodies.responseBody,
          });
        }
      })
      .catch((error: unknown) => {
        console.warn(
          'Failed to fetch request bodies from storage',
          error instanceof Error ? error : new Error(String(error)),
        );
      });

    return () => {
      cancelled = true;
    };
  }, [
    request?.id,
    request?.bodyStorageType,
    request?.bodyRef,
    request?.responseBodyStorageType,
    request?.responseBodyRef,
    fetchBodies,
  ]);

  return useMemo(
    () => ({
      requestBody:
        requestLazyBody.body ||
        dbBodies.requestBody ||
        request?.bodyPreview ||
        request?.body ||
        '',
      responseBody:
        responseLazyBody.body ||
        dbBodies.responseBody ||
        request?.responseBodyPreview ||
        request?.responseBody ||
        '',
    }),
    [
      requestLazyBody.body,
      responseLazyBody.body,
      dbBodies,
      request?.bodyPreview,
      request?.body,
      request?.responseBodyPreview,
      request?.responseBody,
    ],
  );
}
